/**
 * FT-1491: A CHARACTER'S SECTIONS — the client half of
 * shared/protocols/role-sections.ts (the server's validator; its caps are
 * mirrored below so the Studio can say "too long" before the round-trip).
 *
 * A character carries an ordered list of free-titled sections:
 *
 *   { title, text, audience: "everyone" | "storyteller", with? }
 *
 * `with` is another character's id — an official id, or a library role's id
 * (`golemRoleId`) — and the section shows ONLY while that character is on
 * the current script too. That is all a jinx is.
 *
 * Where they live on the client:
 *   - a library row: `sections` (the wire);
 *   - an app role (a script's custom character, or an official the house
 *     overlay has written): `golemSections` — toAppRole / houseRoles put it
 *     there, and it rides the edition frame like every other golem* key.
 *
 * WHO READS WHAT: a storyteller-only section is the storyteller's alone. The
 * edition frame STRIPS them before a player's client ever receives it
 * (`playerSections` — socket.js sendEdition), and `visibleSections` hides
 * them from a player's view besides.
 */

/** Most sections one character may carry (the server's cap). */
export const SECTIONS_MAX = 12;
/** A section title's cap. */
export const SECTION_TITLE_MAX = 40;
/** A section's text cap. */
export const SECTION_TEXT_MAX = 1200;
/** The Studio's quick picks for a title — any title is allowed. */
export const SECTION_TITLE_PICKS = ["Notes", "How to run", "Tips", "Jinx"];

/** A role's sections, whatever carries them. Always an array. */
export function sectionsOf(role) {
  const list = role && (role.golemSections || role.sections);
  return Array.isArray(list)
    ? list.filter((s) => s && typeof s === "object" && s.title)
    : [];
}

/** Is character `id` (an official id or a library id) on the script this
 *  client holds? */
export function onScript(state, id) {
  if (!id || !state || !state.roles) return false;
  if (state.roles.has(id)) return true;
  const same = (role) => !!role && role.isCustom && role.golemRoleId === id;
  for (const role of state.roles.values()) if (same(role)) return true;
  if (state.fabled) {
    for (const role of state.fabled.values()) if (same(role)) return true;
  }
  return false;
}

/** Does this client read as the storyteller? (Outside a town — the script
 *  builder — the author is.) */
export function isStorytellerView(state) {
  return !!(state && state.session && !state.session.isSpectator);
}

/**
 * The sections THIS viewer reads for `role` right now: a storyteller-only one
 * for the storyteller alone, and a linked one only while its partner is on
 * the script.
 */
export function visibleSections(role, state) {
  const storyteller = isStorytellerView(state);
  return sectionsOf(role).filter(
    (s) =>
      (s.audience !== "storyteller" || storyteller) &&
      (!s.with || onScript(state, s.with)),
  );
}

/** A section list with its storyteller-only sections REMOVED — what a
 *  player's client may receive. */
export function playerSections(list) {
  return Array.isArray(list)
    ? list.filter((s) => s && s.audience !== "storyteller")
    : list;
}

/** The storyteller's house overlay (houseRoles.houseFrame) as a player may
 *  receive it: every entry's storyteller-only sections removed. */
export function houseForPlayers(list) {
  return (Array.isArray(list) ? list : []).map((entry) =>
    entry && Array.isArray(entry.sections)
      ? { ...entry, sections: playerSections(entry.sections) }
      : entry,
  );
}

/**
 * The Studio's rows → the wire's list: whitespace tidied the server's way
 * (title trimmed, line endings to "\n", text trimmed), blank rows dropped,
 * only the four stored keys. Caps are the save's own check, not a trim here.
 */
export function cleanSections(list) {
  return (Array.isArray(list) ? list : [])
    .map((s) => {
      const out = {
        title: String((s && s.title) || "").trim(),
        text: String((s && s.text) || "")
          .replace(/\r\n?/g, "\n")
          .trim(),
        audience:
          s && s.audience === "storyteller" ? "storyteller" : "everyone",
      };
      if (s && s.with) out.with = String(s.with);
      return out;
    })
    .filter((s) => s.title || s.text);
}

/** What is wrong with a list the Studio is about to save, in the author's
 *  words — "" when nothing is. Mirrors the server's refusals. */
export function sectionsProblem(list) {
  const clean = cleanSections(list);
  if (clean.length > SECTIONS_MAX) return `At most ${SECTIONS_MAX} sections.`;
  if (clean.some((s) => !s.title)) return "Each section needs a title.";
  if (clean.some((s) => s.title.length > SECTION_TITLE_MAX)) {
    return `Section titles are limited to ${SECTION_TITLE_MAX} characters.`;
  }
  if (clean.some((s) => s.text.length > SECTION_TEXT_MAX)) {
    return `Section text is limited to ${SECTION_TEXT_MAX} characters.`;
  }
  return "";
}
